/**
 * Schaltfläche.
 *
 * Ausgelegt auf Bedienung mit Arbeitshandschuhen (ADR 0001): Mindesthöhe 44 px,
 * Hauptaktionen 56 px. Die Hauptaktion ist graphitfarben, nicht bunt — die
 * gesättigten Farben sind für Status reserviert (Kapitel 11.3), sonst konkurriert
 * ein „Speichern"-Knopf optisch mit „kritisch/offen".
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "dangerOutline";
export type ButtonSize = "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Volle Breite — auf dem Telefon der Regelfall für die Hauptaktion. */
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-ink text-ink-inverted border border-ink hover:bg-ink-muted active:bg-ink-muted disabled:bg-ink-faint disabled:border-ink-faint",
  secondary:
    "bg-surface-raised text-ink border border-line-strong hover:bg-surface-sunken active:bg-surface-sunken",
  ghost: "bg-transparent text-ink border border-transparent hover:bg-surface-sunken",
  danger:
    "bg-status-red-fg text-ink-inverted border border-status-red-fg hover:opacity-90 active:opacity-80",
  // Für Warnungen, die noch keine endgültige Handlung sind — etwa das
  // Abmelden eines lange nicht genutzten Geräts (Kapitel 44): auffällig durch
  // die rote Kontur, aber nicht so gewichtig wie der ausgefüllte `danger`-Knopf.
  dangerOutline:
    "bg-surface-raised text-status-red-fg border border-status-red-fg hover:bg-status-red-bg active:bg-status-red-bg",
};

const SIZES: Record<ButtonSize, string> = {
  md: "min-h-touch px-4 text-[1rem]",
  lg: "min-h-touch-lg px-5 text-[1.0625rem]",
};

export function Button({
  variant = "secondary",
  size = "md",
  block = false,
  loading = false,
  icon,
  disabled,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      // Während des Ladens gesperrt, aber weiter fokussierbar: Ein Knopf, der
      // unter dem Finger verschwindet, verliert den Tastaturfokus (Kapitel 44).
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex select-none items-center justify-center gap-2 rounded-card font-medium",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-70",
        // Kein Hervorheben beim Antippen: Der blaue Standardschimmer mobiler
        // Browser wirkt wie ein Statuswechsel.
        "[-webkit-tap-highlight-color:transparent] touch-manipulation",
        VARIANTS[variant],
        SIZES[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === "lg" ? 22 : 18} /> : icon}
      {children}
    </button>
  );
}
